/**
 * AuthProvider + useAuth hook.
 *
 * Lifecycle on mount:
 *   1. Read token from localStorage. None → status="unauthenticated".
 *   2. Token present → status="bootstrapping" while we round-trip /me.
 *      Success → "authenticated" + user.
 *      401 / network error → clear token + "unauthenticated".
 *
 * The api/client.ts request wrapper subscribes to token changes via
 * `setRequestToken()` so it can attach the Authorization header without
 * importing this context (avoiding a circular dep).
 */
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { setRequestToken, subscribeToUnauthorized } from "../api/client";
import { AuthError, fetchCurrentUser, login as loginApi } from "./api";
import { clearToken, readToken, writeToken } from "./storage";
import type { AuthState, User } from "./types";

interface AuthContextValue extends AuthState {
    /** Run the OAuth2 password grant + /me round-trip. Throws on failure
     *  so the login form can render the message inline. */
    login: (email: string, password: string) => Promise<void>;
    /** Tear down: clear token + flip back to unauthenticated. Cheap. */
    logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<AuthState>(() => {
        const token = readToken();
        return { status: token ? "bootstrapping" : "unauthenticated", user: null };
    });

    // Stable refs the api client can call without re-subscribing each
    // render. Set once on mount, kept in sync below.
    const tokenRef = useRef<string | null>(readToken());
    // Wire the api-client header in an effect so we never read the ref
    // during render — satisfies the React refs rule.
    useEffect(() => {
        setRequestToken(tokenRef.current);
    }, []);

    const applyToken = useCallback((next: string | null) => {
        tokenRef.current = next;
        setRequestToken(next);
        if (next) writeToken(next);
        else clearToken();
    }, []);

    const logout = useCallback(() => {
        applyToken(null);
        setState({ status: "unauthenticated", user: null });
    }, [applyToken]);

    // Bootstrap — only runs when there's a token in storage at mount.
    useEffect(() => {
        const token = tokenRef.current;
        if (!token) return;

        let cancelled = false;
        fetchCurrentUser()
            .then((user) => {
                if (cancelled) return;
                setState({ status: "authenticated", user });
            })
            .catch(() => {
                if (cancelled) return;
                applyToken(null);
                setState({ status: "unauthenticated", user: null });
            });
        return () => {
            cancelled = true;
        };
    }, [applyToken]);

    // Global 401 handling — any authenticated request that comes back
    // 401 means the token expired or got revoked. Single source of
    // truth for "you got logged out": flush state, the protected route
    // takes care of the redirect.
    useEffect(() => subscribeToUnauthorized(logout), [logout]);

    const login = useCallback(
        async (email: string, password: string) => {
            const { accessToken } = await loginApi(email, password);
            applyToken(accessToken);
            try {
                const user = await fetchCurrentUser();
                setState({ status: "authenticated", user });
            } catch (err) {
                applyToken(null);
                setState({ status: "unauthenticated", user: null });
                throw err instanceof Error ? err : new AuthError(500, "Failed to load user");
            }
        },
        [applyToken],
    );

    const value: AuthContextValue = { ...state, login, logout };
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook + provider co-located on purpose
export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
    return ctx;
}

export type { User };
